import React from 'react';

/**
 * PaymentLogos — official Stripe wordmark + card network logos as inline SVGs.
 *
 * Used wherever NoFee displays payment-processor branding (Hero, payout
 * sections, trust banners, etc.). Single source of truth: tweaking sizes
 * or colors should happen ONLY here.
 *
 * Each component accepts `className` for sizing and an optional `monochrome`
 * prop that flips the fill to soft white-ish — useful when sitting inside
 * a colored badge or when we want them to feel quieter on dense layouts.
 */

export const StripeWordmark = ({ className = 'h-5 w-auto', monochrome = false }) => (
  <svg
    viewBox="0 0 60 25"
    className={className}
    fill="none"
    role="img"
    aria-label="Stripe"
  >
    <path
      d="M59.5 14.4c0-4.3-2.1-7.7-6.1-7.7s-6.4 3.4-6.4 7.7c0 5.1 2.9 7.6 7 7.6 2 0 3.5-.5 4.7-1.1v-3.4c-1.2.6-2.5 1-4.2 1-1.7 0-3.1-.6-3.3-2.5h8.3v-1.6zm-8.4-1.6c0-1.9 1.2-2.7 2.2-2.7 1.1 0 2.2.8 2.2 2.7h-4.4zM41.5 6.7c-1.7 0-2.7.8-3.3 1.3l-.2-1H34V26l4.2-.9V21c.6.4 1.5 1 3 1 3.1 0 5.9-2.5 5.9-7.7-.1-4.7-3-7.6-5.6-7.6zm-1 11.5c-1 0-1.6-.4-2-.8v-6.5c.5-.4 1.1-.8 2-.8 1.6 0 2.7 1.8 2.7 4 0 2.4-1.1 4.1-2.7 4.1zM33.2 5.4l4.2-.9V1L33.2 2v3.4zM33.2 7h4.2v14.7h-4.2V7zM28.7 8.2L28.4 7h-3.6v14.7h4.2v-10c1-1.3 2.7-1 3.2-.9V7c-.6-.2-2.5-.6-3.5 1.2zM20.2 3.4l-4.1.9v13.5c0 2.5 1.9 4.3 4.4 4.3 1.4 0 2.4-.3 3-.6V18c-.5.2-3.3 1-3.3-1.5V10.5h3.3V7h-3.3V3.4zM4.2 11.1c0-.7.5-.9 1.4-.9 1.2 0 2.8.4 4 1.1V7.4c-1.3-.5-2.6-.8-4-.8C2.4 6.6 0 8.4 0 11.4c0 4.7 6.3 3.9 6.3 5.9 0 .8-.7 1-1.6 1-1.3 0-3-.6-4.3-1.3v4c1.5.6 2.9.9 4.3.9 3.4 0 5.9-1.6 5.9-4.7-.1-5-6.4-4.1-6.4-6.1z"
      fill={monochrome ? 'currentColor' : '#635BFF'}
    />
  </svg>
);

export const PayPalWordmark = ({ className = 'h-5 w-auto' }) => null;

// ──────────────────────────────────────────────────────────────────────────
// Network logos — Visa / Mastercard / Apple Pay / Google Pay
// All inline SVGs; brand-accurate fills; same className/monochrome API.
// ──────────────────────────────────────────────────────────────────────────

export const VisaLogo = ({ className = 'h-5 w-auto', monochrome = false }) => (
  <svg viewBox="0 0 48 16" className={className} fill="none" role="img" aria-label="Visa">
    <path
      d="M20.4 0.3l-4.6 11.5h-3l-2.3-8.7c-.1-.6-.3-.8-.7-1C9.1 1.7 7.9 1.4 6.8 1.2L6.9.3h4.9c.6 0 1.2.4 1.3 1.1l1.2 6.5L17.4.3h3zm11.9 7.7c0-2.9-4-3.1-4-4.4 0-.4.4-.8 1.2-.9.4-.1 1.5-.1 2.7.5l.5-2.4C32.1.5 31.3.3 30.3.3c-2.8 0-4.7 1.5-4.7 3.6 0 1.6 1.4 2.4 2.5 2.9 1.1.5 1.4.9 1.4 1.3 0 .7-.8 1-1.6 1-1.3 0-2.1-.4-2.7-.6l-.5 2.5c.6.3 1.8.5 3.1.5 3 0 4.9-1.5 4.9-3.5zm7.4 3.8h2.6L40.1.3h-2.4c-.5 0-1 .3-1.2.8L32.2 11.8h3l.6-1.6h3.7l.2 1.6zm-3.2-3.8l1.5-4.2.9 4.2h-2.4zM24.4.3l-2.4 11.5h-2.9L21.5.3h2.9z"
      fill={monochrome ? 'currentColor' : '#1A1F71'}
    />
  </svg>
);

export const MastercardLogo = ({ className = 'h-5 w-auto', monochrome = false }) => (
  <svg viewBox="0 0 32 20" className={className} fill="none" role="img" aria-label="Mastercard">
    <circle cx="12" cy="10" r="7" fill={monochrome ? 'currentColor' : '#EB001B'} />
    <circle cx="20" cy="10" r="7" fill={monochrome ? 'currentColor' : '#F79E1B'} fillOpacity={monochrome ? 0.6 : 1} />
    <path
      d="M16 5.2c1.4 1.3 2.3 3.1 2.3 4.8 0 1.7-.9 3.5-2.3 4.8-1.4-1.3-2.3-3.1-2.3-4.8 0-1.7.9-3.5 2.3-4.8z"
      fill={monochrome ? 'currentColor' : '#FF5F00'}
    />
  </svg>
);

export const ApplePayLogo = ({ className = 'h-5 w-auto', monochrome = false }) => (
  <svg viewBox="0 0 48 20" className={className} fill="none" role="img" aria-label="Apple Pay">
    <path
      d="M8.7 3.2c.5-.6.8-1.5.7-2.3-.7 0-1.5.5-2 1.1-.4.5-.8 1.3-.7 2.1.8.1 1.5-.3 2-.9zm.7 1.1c-1.1-.1-2.1.6-2.6.6-.6 0-1.4-.6-2.3-.6-1.2 0-2.3.7-2.9 1.8-1.2 2.1-.3 5.3.9 7 .6.8 1.3 1.8 2.2 1.7.9 0 1.2-.6 2.3-.6 1.1 0 1.4.6 2.3.6 1 0 1.6-.8 2.2-1.7.7-1 1-1.9 1-1.9-.1-.1-2-.7-2-2.8 0-1.7 1.4-2.6 1.5-2.6-.8-1.2-2.1-1.4-2.6-1.5zM18.2 2.5c2.5 0 4.2 1.7 4.2 4.2s-1.8 4.2-4.3 4.2h-2.7v4.4h-2V2.5h4.8zm-2.8 6.7h2.3c1.7 0 2.7-.9 2.7-2.5s-1-2.5-2.7-2.5h-2.3v5zM23 12.6c0-1.7 1.3-2.7 3.6-2.9l2.6-.1v-.7c0-1.1-.7-1.7-2-1.7-1 0-1.8.5-2 1.3h-1.8c.1-1.7 1.6-3 3.8-3 2.4 0 4 1.3 4 3.2v6.6h-1.9v-1.6c-.5 1-1.7 1.7-3 1.7-1.9 0-3.3-1.1-3.3-2.8zm6.2-.9v-.7l-2.3.1c-1.2.1-1.9.6-1.9 1.5 0 .8.7 1.4 1.7 1.4 1.4 0 2.5-1 2.5-2.3zM33.5 18.6V17c.1 0 .4.1.6.1 1 0 1.5-.4 1.9-1.4l.2-.6L33 6.4h2.1l2.2 7.1 2.2-7.1h2.1l-3.4 9.5c-.8 2.2-1.7 2.8-3.6 2.8-.1 0-.6 0-1.1-.1z"
      fill={monochrome ? 'currentColor' : '#000'}
    />
  </svg>
);

export const GooglePayLogo = ({ className = 'h-5 w-auto', monochrome = false }) => (
  <svg viewBox="0 0 52 20" className={className} fill="none" role="img" aria-label="Google Pay">
    <path d="M24.6 9.8v5.6h-1.8V1.6h4.7c1.2 0 2.2.4 3 1.2.8.8 1.2 1.8 1.2 3s-.4 2.2-1.2 3c-.8.8-1.8 1.2-3 1.2h-2.9zm0-6.5v4.8h3c.7 0 1.3-.2 1.7-.7.5-.5.7-1 .7-1.7s-.2-1.2-.7-1.7c-.5-.5-1-.7-1.7-.7h-3z" fill={monochrome ? 'currentColor' : '#5F6368'} />
    <path d="M35.3 5.7c1.4 0 2.5.4 3.3 1.1.8.7 1.2 1.7 1.2 3v6.1h-1.8v-1.4h-.1c-.8 1.1-1.8 1.7-3.1 1.7-1.1 0-2-.3-2.7-1-.7-.6-1.1-1.5-1.1-2.4 0-1 .4-1.9 1.2-2.5.8-.6 1.8-.9 3.2-.9 1.2 0 2.1.2 2.9.7v-.4c0-.7-.3-1.2-.8-1.7s-1.2-.7-1.9-.7c-1.1 0-2 .5-2.6 1.4l-1.7-1c1.1-1.4 2.5-2 4-2zm-2.4 7.2c0 .5.2.9.6 1.2.4.3.9.5 1.4.5.8 0 1.5-.3 2.1-.9.6-.6.9-1.3.9-2.1-.7-.5-1.6-.8-2.7-.8-.8 0-1.5.2-2.1.6-.6.4-.9.9-.9 1.5zM51.1 6l-6 13.8h-1.9l2.2-4.8L41.5 6h2l2.9 6.9L49.1 6h2z" fill={monochrome ? 'currentColor' : '#5F6368'} />
    <path d="M16.4 9.4c0-.6-.1-1.2-.2-1.7H8.5v3.3h4.5c-.2 1.1-.8 2-1.7 2.6v2.1h2.7c1.6-1.5 2.4-3.6 2.4-6.3z" fill={monochrome ? 'currentColor' : '#4285F4'} />
    <path d="M8.5 17.4c2.3 0 4.2-.8 5.5-2.1l-2.7-2.1c-.7.5-1.7.8-2.8.8-2.2 0-4-1.5-4.6-3.4H1.1v2.2c1.4 2.7 4.2 4.6 7.4 4.6z" fill={monochrome ? 'currentColor' : '#34A853'} />
    <path d="M3.9 10.6c-.2-.5-.2-1.1-.2-1.6 0-.6.1-1.1.2-1.6V5.2H1.1c-.6 1.1-.9 2.4-.9 3.8 0 1.4.3 2.7.9 3.8l2.8-2.2z" fill={monochrome ? 'currentColor' : '#FBBC04'} />
    <path d="M8.5 4c1.3 0 2.4.4 3.3 1.3l2.4-2.4C12.7 1.5 10.8.6 8.5.6 5.3.6 2.5 2.5 1.1 5.2l2.8 2.2C4.5 5.5 6.3 4 8.5 4z" fill={monochrome ? 'currentColor' : '#EA4335'} />
  </svg>
);

/**
 * PaymentNetworkRow — premium row of all supported networks.
 * Used on checkout/trust pages to maximize visual trust without clutter.
 */
export const PaymentNetworkRow = ({ className = '', logoClass = 'h-5 w-auto', monochrome = false }) => (
  <div
    className={`flex items-center gap-4 sm:gap-5 flex-wrap ${className}`}
    data-testid="payment-network-row"
  >
    <VisaLogo className={logoClass} monochrome={monochrome} />
    <MastercardLogo className={logoClass} monochrome={monochrome} />
    <ApplePayLogo className={logoClass} monochrome={monochrome} />
    <GooglePayLogo className={logoClass} monochrome={monochrome} />
  </div>
);



/**
 * PaymentLogosRow — inline Stripe brand row.
 * Drops into any line of copy as a single, balanced unit.
 */
export const PaymentLogosRow = ({
  className = '',
  logoClass = 'h-4 sm:h-[18px] w-auto',
}) => (
  <span
    className={`inline-flex items-center gap-2 sm:gap-2.5 align-middle ${className}`}
    data-testid="payment-logos-row"
  >
    <StripeWordmark className={logoClass} />
  </span>
);

export default PaymentLogosRow;
